import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import jsQR from 'jsqr';

interface QrScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const InbuiltQrScannerModal: React.FC<QrScannerModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [cameraActive, setCameraActive] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanMessage, setScanMessage] = useState<{ type: 'info' | 'error' | 'success'; text: string } | null>(null);
  const [manualId, setManualId] = useState('');
  const [error, setError] = useState('');
  const [autoScan, setAutoScan] = useState(false);

  // Clean teardown for media stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignore
        }
      });
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setAnalyzing(false);
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    setScanMessage(null);
    setError('');
    onClose();
  }, [stopCamera, onClose]);

  // Navigate according to QR payload
  const navigateFromQr = useCallback((qrData: string) => {
    const raw = qrData.trim();
    if (raw.includes('/tag/')) {
      const parts = raw.split('/tag/');
      const tagId = parts[parts.length - 1];
      navigate(`/tag/${tagId}`);
    } else if (raw.toUpperCase().startsWith('VRS-TAG')) {
      navigate(`/tag/${raw}`);
    } else if (raw.includes('/item/')) {
      const parts = raw.split('/item/');
      const itemId = parts[parts.length - 1];
      navigate(`/item/${itemId}`);
    } else if (raw.includes('/asset/')) {
      const parts = raw.split('/asset/');
      const assetId = parts[parts.length - 1];
      navigate(`/item/${assetId}`);
    } else if (raw.includes('/verisure/cabinet/')) {
      const parts = raw.split('/verisure/cabinet/');
      const cabinetId = parts[parts.length - 1];
      navigate(`/verisure/cabinet/${cabinetId}`);
    } else if (raw.toUpperCase().startsWith('ITM-')) {
      navigate(`/item/${raw}`);
    } else if (raw.toLowerCase().includes('cab-') || raw.toUpperCase().startsWith('CAB-')) {
      const match = raw.match(/cab-\w+/i);
      navigate(`/verisure/cabinet/${match ? match[0] : raw}`);
    } else {
      navigate(`/item/${raw}`);
    }
  }, [navigate]);

  // Gentle audio chime on code recognition
  const playChime = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.12, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.16);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.17);
    } catch {
      // Audio context may be restricted by browser before gesture
    }
  };

  const handleQrSuccess = useCallback((qrData: string) => {
    playChime();
    setScanMessage({ type: 'success', text: `QR Code Verified: ${qrData}` });

    setTimeout(() => {
      stopCamera();
      onClose();
      navigateFromQr(qrData);
    }, 320);
  }, [stopCamera, onClose, navigateFromQr]);

  // Decode a single frame (from video into hidden canvas)
  const captureAndScanFrame = useCallback(async (): Promise<string | null> => {
    const video = videoRef.current;
    if (!video || video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return null;
    }

    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    if (w === 0 || h === 0) return null;

    // 1. Try Native BarcodeDetector if available
    if ('BarcodeDetector' in window) {
      try {
        // @ts-ignore
        const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        const barcodes = await detector.detect(video);
        if (barcodes.length > 0 && barcodes[0].rawValue) {
          return barcodes[0].rawValue;
        }
      } catch {
        // Fallback to jsQR
      }
    }

    // 2. High-Fidelity jsQR
    const canvas = canvasRef.current || document.createElement('canvas');
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0, w, h);
    const imgData = ctx.getImageData(0, 0, w, h);
    const code = jsQR(imgData.data, imgData.width, imgData.height, {
      inversionAttempts: 'attemptBoth'
    });

    if (code && code.data) {
      return code.data;
    }

    return null;
  }, []);

  // Manual Trigger: User clicks "Scan QR Code"
  const handleManualScanClick = async () => {
    if (!cameraActive || analyzing) return;

    setAnalyzing(true);
    setScanMessage(null);

    try {
      const detected = await captureAndScanFrame();
      if (detected) {
        handleQrSuccess(detected);
      } else {
        setScanMessage({
          type: 'error',
          text: 'No QR code detected in this snapshot. Center the QR code and tap Scan again.'
        });
      }
    } catch (err) {
      console.error('Manual frame scan error:', err);
      setScanMessage({
        type: 'error',
        text: 'Could not read camera frame. Please ensure camera permission is granted.'
      });
    } finally {
      setAnalyzing(false);
    }
  };

  // Safe Camera Lifecycle: starts only once when modal opens, stops cleanly when closed
  useEffect(() => {
    if (!isOpen) {
      stopCamera();
      setScanMessage(null);
      setError('');
      return;
    }

    let isCancelled = false;
    setError('');
    setScanMessage(null);

    const initCamera = async () => {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Camera access requires HTTPS or is unsupported on this browser.');
        }

        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: 'environment' },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          });
        } catch {
          // Fallback for laptops/desktops
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }

        if (isCancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
        setCameraActive(true);
      } catch (err: any) {
        if (!isCancelled) {
          console.error('Camera startup error:', err);
          setError('Camera could not be accessed. Check browser permissions or enter ID manually below.');
        }
      }
    };

    initCamera();

    const videoEl = videoRef.current;
    return () => {
      isCancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoEl) {
        videoEl.srcObject = null;
      }
      setCameraActive(false);
    };
  }, [isOpen, stopCamera]);

  // Optional Auto-Scan interval (only if user explicitly enables auto-scan)
  useEffect(() => {
    if (!autoScan || !cameraActive || !isOpen) return;

    let timer: NodeJS.Timeout;
    const runAutoScan = async () => {
      const code = await captureAndScanFrame();
      if (code) {
        handleQrSuccess(code);
      } else {
        timer = setTimeout(runAutoScan, 250); // calm 4Hz poll, no rapid 60fps loop
      }
    };

    timer = setTimeout(runAutoScan, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [autoScan, cameraActive, isOpen, captureAndScanFrame, handleQrSuccess]);

  // Escape key handler to exit cleanly
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Form submit for manual ID
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualId.trim()) return;
    stopCamera();
    onClose();
    navigateFromQr(manualId.trim());
  };

  if (!isOpen) return null;

  // Render via React Portal directly into document.body to prevent clipping by navbar or parent styles
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        // Close if clicked directly on dark backdrop
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      {/* Centered Modal Card (my-auto ensures full visibility without cut-off) */}
      <div className="my-auto relative max-w-md landscape:max-w-2xl w-full bg-slate-900 border border-slate-700/80 rounded-3xl p-4 sm:p-6 text-white shadow-2xl transition-all max-h-[94vh] overflow-y-auto overscroll-contain">
        {/* Top Header */}
        <div className="flex items-center justify-between mb-3.5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-orange-500/10 border border-orange-500/30 flex items-center justify-center text-orange-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="3" width="7" height="7" rx="1" strokeWidth="2"/>
                <rect x="14" y="3" width="7" height="7" rx="1" strokeWidth="2"/>
                <rect x="14" y="14" width="7" height="7" rx="1" strokeWidth="2"/>
                <rect x="3" y="14" width="7" height="7" rx="1" strokeWidth="2"/>
              </svg>
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white tracking-tight leading-tight">
                Verisure QR Scanner
              </h2>
              <p className="text-[11px] text-slate-400">Position the QR code inside the frame</p>
            </div>
          </div>

          {/* Explicit 'X' Exit Button */}
          <button
            type="button"
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center text-lg transition shadow-sm"
            aria-label="Close Scanner"
            title="Exit Scanner (Esc)"
          >
            &times;
          </button>
        </div>

        {/* Responsive Grid for Portrait vs Landscape */}
        <div className="landscape:grid landscape:grid-cols-2 landscape:gap-4 items-center">
          {/* Left Column in Landscape: Camera Viewfinder */}
          <div>
            {error ? (
              <div className="mb-4 p-4 bg-red-950/60 border border-red-800 text-red-300 rounded-2xl text-xs text-center leading-relaxed">
                {error}
              </div>
            ) : (
              <div className="relative w-full aspect-[4/3] landscape:aspect-[16/11] bg-black rounded-2xl overflow-hidden border border-slate-700/80 shadow-inner mb-4 landscape:mb-0">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />

                {/* Stable, Solid Target Reticles (Zero Blinking) */}
                <div className="absolute inset-0 pointer-events-none p-5 flex flex-col justify-between">
                  <div className="flex justify-between">
                    <div className={`w-8 h-8 border-t-3 border-l-3 rounded-tl-xl transition-colors duration-200 ${
                      scanMessage?.type === 'success' ? 'border-emerald-400' : 'border-orange-500'
                    }`} />
                    <div className={`w-8 h-8 border-t-3 border-r-3 rounded-tr-xl transition-colors duration-200 ${
                      scanMessage?.type === 'success' ? 'border-emerald-400' : 'border-orange-500'
                    }`} />
                  </div>
                  <div className="flex justify-between">
                    <div className={`w-8 h-8 border-b-3 border-l-3 rounded-bl-xl transition-colors duration-200 ${
                      scanMessage?.type === 'success' ? 'border-emerald-400' : 'border-orange-500'
                    }`} />
                    <div className={`w-8 h-8 border-b-3 border-r-3 rounded-br-xl transition-colors duration-200 ${
                      scanMessage?.type === 'success' ? 'border-emerald-400' : 'border-orange-500'
                    }`} />
                  </div>
                </div>

                {/* Hidden canvas used for on-demand frame decoding */}
                <canvas ref={canvasRef} className="hidden" />

                {/* State Pill */}
                {!cameraActive && (
                  <div className="absolute inset-0 bg-slate-950/80 flex items-center justify-center text-xs text-slate-400 font-mono">
                    Connecting to camera...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column in Landscape: Status, Controls, Manual Fallback */}
          <div>
            {/* Scan Message / Inline Status */}
            {scanMessage && (
              <div
                className={`mb-3 p-2.5 rounded-xl text-xs text-center font-medium transition-all ${
                  scanMessage.type === 'success'
                    ? 'bg-emerald-950/70 border border-emerald-700 text-emerald-300'
                    : 'bg-amber-950/70 border border-amber-800/80 text-amber-200'
                }`}
              >
                {scanMessage.text}
              </div>
            )}

            {/* Primary Controls: Big "Scan QR Code" button + Mode Toggle */}
            <div className="space-y-3 mb-4">
              <button
                type="button"
                disabled={!cameraActive || analyzing}
                onClick={handleManualScanClick}
                className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 text-white font-bold py-3 px-4 rounded-xl text-sm shadow-lg shadow-orange-500/25 transition disabled:opacity-50 flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{analyzing ? 'Analyzing Frame...' : 'Scan / Capture QR Code'}</span>
              </button>

              {/* Secondary Controls Bar: Mode Switcher & Exit Button */}
              <div className="flex items-center justify-between gap-2 pt-1 text-xs">
                <button
                  type="button"
                  onClick={() => setAutoScan((prev) => !prev)}
                  className={`px-3 py-1.5 rounded-lg border text-[11px] font-semibold transition flex items-center gap-1.5 ${
                    autoScan
                      ? 'bg-orange-500/20 text-orange-400 border-orange-500/40'
                      : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'
                  }`}
                  title="Toggle automatic scanning mode"
                >
                  <span className={`w-2 h-2 rounded-full ${autoScan ? 'bg-orange-400 animate-pulse' : 'bg-slate-500'}`}></span>
                  <span>Auto-Scan: {autoScan ? 'ON' : 'OFF'}</span>
                </button>

                <button
                  type="button"
                  onClick={handleClose}
                  className="px-3.5 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 hover:text-white text-[11px] font-semibold transition"
                >
                  Cancel & Exit
                </button>
              </div>
            </div>

            {/* Manual ID Input Fallback */}
            <form onSubmit={handleManualSubmit} className="border-t border-slate-800/90 pt-3">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 text-center">
                Or Type Asset / Cabinet ID
              </label>
              <div className="flex gap-2">
            <input
              type="text"
              placeholder="e.g. cab-101 or VRS-TAG-101"
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              className="flex-1 bg-slate-950 border border-slate-800 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-orange-500 transition placeholder:text-slate-600"
            />
            <button
              type="submit"
              className="bg-slate-800 hover:bg-slate-700 text-orange-400 border border-orange-500/30 font-bold px-3.5 py-2 rounded-xl text-xs transition"
            >
              Open &rarr;
            </button>
          </div>
        </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
